/**
 * Admin Page
 *
 * Super admin page for managing organizations.
 * Only accessible to users with isSuperAdmin: true AND @cleverbear.com email.
 *
 * @module pages/admin/AdminPage
 */

import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAdmin, type OrganizationSummary } from '@/hooks';
import { usePageTitle } from '@/hooks';
import {
  Card,
  CardHeader,
  Button,
  Badge,
  Input,
  Modal,
  Spinner,
  useToast,
} from '@/components/ui';
import {
  Search,
  Building2,
  RefreshCw,
  ChevronRight,
  Shield,
  Users,
  Zap,
} from 'lucide-react';

// =============================================================================
// Helper Functions
// =============================================================================

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatNumber(num: number): string {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toString();
}

function getAccountTypeBadgeVariant(
  accountType: 'standard' | 'internal'
): 'default' | 'primary' {
  return accountType === 'internal' ? 'primary' : 'default';
}

function getPlanBadgeVariant(
  planId: string
): 'default' | 'primary' | 'outline' {
  switch (planId) {
    case 'enterprise':
    case 'elite':
      return 'primary';
    case 'pro':
    case 'starter':
      return 'default';
    default:
      return 'outline';
  }
}

// =============================================================================
// Organization Row Component
// =============================================================================

interface OrgRowProps {
  org: OrganizationSummary;
  onToggleAccountType: (org: OrganizationSummary) => void;
}

function OrgRow({ org, onToggleAccountType }: OrgRowProps) {
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            <Building2 className="h-4 w-4 text-gray-500" />
          </div>
          <div>
            <p className="font-medium text-gray-900">{org.name}</p>
            <p className="text-sm text-gray-500">{org.slug}</p>
          </div>
        </div>
      </td>
      <td className="py-4 px-4">
        <p className="text-sm text-gray-600">{org.ownerEmail || 'Unknown'}</p>
      </td>
      <td className="py-4 px-4">
        <Badge variant={getAccountTypeBadgeVariant(org.accountType)}>
          {org.accountType === 'internal' ? 'Internal' : 'Standard'}
        </Badge>
      </td>
      <td className="py-4 px-4">
        <Badge variant={getPlanBadgeVariant(org.planId)}>
          {org.planId.charAt(0).toUpperCase() + org.planId.slice(1)}
        </Badge>
      </td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-1 text-sm text-gray-600">
          <Users className="h-3.5 w-3.5" />
          {org.memberCount}
        </div>
      </td>
      <td className="py-4 px-4">
        <div className="text-sm">
          <p className="text-gray-600">{formatNumber(org.usageThisPeriod)} / period</p>
          <p className="text-gray-400">{formatNumber(org.lifetimeUsage)} lifetime</p>
        </div>
      </td>
      <td className="py-4 px-4 text-sm text-gray-500">
        {formatDate(org.createdAt)}
      </td>
      <td className="py-4 px-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onToggleAccountType(org)}
        >
          {org.accountType === 'internal' ? 'Set Standard' : 'Set Internal'}
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </td>
    </tr>
  );
}

// =============================================================================
// Confirm Modal Component
// =============================================================================

interface ConfirmModalProps {
  isOpen: boolean;
  org: OrganizationSummary | null;
  newAccountType: 'standard' | 'internal';
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmModal({
  isOpen,
  org,
  newAccountType,
  loading,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  if (!org) return null;

  const isSettingInternal = newAccountType === 'internal';

  return (
    <Modal
      open={isOpen}
      onClose={onCancel}
      title={isSettingInternal ? 'Set as Internal Account' : 'Set as Standard Account'}
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <Building2 className="h-5 w-5 text-gray-500" />
          <div>
            <p className="font-medium">{org.name}</p>
            <p className="text-sm text-gray-500">{org.ownerEmail}</p>
          </div>
        </div>

        {isSettingInternal ? (
          <div className="space-y-2">
            <p className="text-sm text-gray-600">
              Setting this organization as <strong>internal</strong> will:
            </p>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>Bypass all rate limits (unlimited requests)</li>
              <li>Skip Stripe billing (free usage)</li>
              <li>Still track usage for analytics</li>
            </ul>
            <p className="text-sm text-warning-600 mt-3">
              Only use this for Hummingbird internal accounts (e.g., marketing demos).
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-gray-600">
              Setting this organization as <strong>standard</strong> will:
            </p>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>Apply normal rate limits based on plan</li>
              <li>Enable Stripe billing</li>
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="ghost" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant={isSettingInternal ? 'primary' : 'secondary'}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Updating...
              </>
            ) : (
              <>
                {isSettingInternal ? <Zap className="h-4 w-4 mr-2" /> : null}
                Confirm
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// =============================================================================
// Admin Page Component
// =============================================================================

export function AdminPage() {
  usePageTitle('Admin');

  const { isSuperAdmin, loading: authLoading } = useAuth();
  const {
    organizations,
    loading,
    error,
    nextCursor,
    fetchOrganizations,
    loadMore,
    setAccountType,
    refresh,
  } = useAdmin();
  const { addToast } = useToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterAccountType, setFilterAccountType] = useState<'all' | 'standard' | 'internal'>('all');
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    org: OrganizationSummary | null;
    newAccountType: 'standard' | 'internal';
  }>({
    isOpen: false,
    org: null,
    newAccountType: 'standard',
  });
  const [updating, setUpdating] = useState(false);

  // ---------------------------------------------------------------------------
  // Initial Load
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (isSuperAdmin) {
      fetchOrganizations({
        limit: 50,
        search: searchQuery || undefined,
        accountType: filterAccountType === 'all' ? undefined : filterAccountType,
      });
    }
  }, [isSuperAdmin, fetchOrganizations, searchQuery, filterAccountType]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchOrganizations({
      limit: 50,
      search: searchQuery || undefined,
      accountType: filterAccountType === 'all' ? undefined : filterAccountType,
    });
  };

  const handleToggleAccountType = (org: OrganizationSummary) => {
    const newAccountType = org.accountType === 'internal' ? 'standard' : 'internal';
    setConfirmModal({
      isOpen: true,
      org,
      newAccountType,
    });
  };

  const handleConfirm = async () => {
    if (!confirmModal.org) return;

    setUpdating(true);
    try {
      await setAccountType(confirmModal.org.id, confirmModal.newAccountType);
      addToast('success', `Account type updated to ${confirmModal.newAccountType}`);
      setConfirmModal({ isOpen: false, org: null, newAccountType: 'standard' });
    } catch (err) {
      console.error('Error updating account type:', err);
      addToast('error', 'Failed to update account type. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    setConfirmModal({ isOpen: false, org: null, newAccountType: 'standard' });
  };

  // ---------------------------------------------------------------------------
  // Access Control
  // ---------------------------------------------------------------------------

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  // Redirect non-super-admins
  if (!isSuperAdmin) {
    return <Navigate to="/overview" replace />;
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-brand-100 rounded-lg">
            <Shield className="h-5 w-5 text-brand-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Admin</h1>
            <p className="text-sm text-gray-500">Manage organizations</p>
          </div>
        </div>

        <Button variant="ghost" onClick={refresh} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <div className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by name, slug, or owner email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <select
              value={filterAccountType}
              onChange={(e) =>
                setFilterAccountType(e.target.value as 'all' | 'standard' | 'internal')
              }
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value="all">All Types</option>
              <option value="standard">Standard</option>
              <option value="internal">Internal</option>
            </select>

            <Button type="submit" variant="secondary">
              Search
            </Button>
          </form>
        </div>
      </Card>

      {/* Error */}
      {error && (
        <div className="p-4 bg-error-50 border border-error-200 rounded-lg text-error-700">
          {error}
        </div>
      )}

      {/* Organizations Table */}
      <Card>
        <CardHeader
          title="Organizations"
          description={`${organizations.length} organization${organizations.length !== 1 ? 's' : ''}`}
        />

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Organization
                </th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Owner
                </th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Members
                </th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Usage
                </th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && organizations.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center">
                    <Spinner size="lg" />
                  </td>
                </tr>
              ) : organizations.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-gray-500">
                    No organizations found.
                  </td>
                </tr>
              ) : (
                organizations.map((org) => (
                  <OrgRow
                    key={org.id}
                    org={org}
                    onToggleAccountType={handleToggleAccountType}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Load More */}
        {nextCursor && (
          <div className="p-4 border-t border-gray-200 text-center">
            <Button variant="ghost" onClick={loadMore} disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Loading...
                </>
              ) : (
                'Load More'
              )}
            </Button>
          </div>
        )}
      </Card>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        org={confirmModal.org}
        newAccountType={confirmModal.newAccountType}
        loading={updating}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}
